import React from "react";
import { Search } from "lucide-react";
import { StatusBadge, PriorityBadge } from "../components/Layout";
import {
  Box,
  Typography,
  Paper as MuiPaper,
  useTheme,
  useMediaQuery,
  Card as MuiCard,
} from "@mui/material";
import { spacing } from "@mui/system";
import styled from "@emotion/styled";
import { DataGrid } from "@mui/x-data-grid";
import Filters from "../../rcAuto/Filters";
import { formatDate } from "../lib/utils";
import { useNavigate } from "react-router-dom";

const Paper = styled(MuiPaper)(spacing);
const Card = styled(MuiCard)(spacing);

export default function TicketList({
  rows,
  pageSize,
  totalRows,
  setPageSize,
  pageNumber,
  setPageNumber,
  filters,
  filtersActive,
  switchFilterActive,
  filterData,
  setFilterData,
  handleApplyFilters,
  handleRemoveFilters,
  filtersLoading,
  isRemovingFilters,
  isAnyFieldFilled,
  canClearFilters,
  loading,
}) {
  const navigate = useNavigate();
  const columns = [
    {
      field: "codice",
      headerName: "ID",
      minWidth: 100,
      flex: 1,
      headerAlign: "center",
      align: "center",
      renderCell: (params) => (
        <Box display="flex" flexDirection="column">
          <Typography
            fontWeight="bold"
            color="primary"
            textTransform="uppercase"
            letterSpacing="-0.025em"
            onClick={() => navigate(`/pages/supporto/${params.row.idUid}`)}
            sx={{
              cursor: "pointer",
              ":hover": { textDecoration: "underline" },
            }}
          >
            {params.row.codice}
          </Typography>
        </Box>
      ),
    },
    {
      field: "titolo",
      headerName: "Oggetto",
      minWidth: 300,
      flex: 1,
    },
    {
      field: "tipo",
      headerName: "Tipo",
      minWidth: 150,
      flex: 1,
      headerAlign: "center",
      align: "center",
    },
    {
      field: "categoria",
      headerName: "Categoria",
      minWidth: 150,
      flex: 1,
      headerAlign: "center",
      align: "center",
    },
    {
      field: "stato",
      headerName: "Stato",
      minWidth: 150,
      flex: 1,
      headerAlign: "center",
      align: "center",
      renderCell: (params) => <StatusBadge status={params.row.stato} />,
    },
    {
      field: "priorita",
      headerName: "Priorità",
      minWidth: 150,
      flex: 1,
      headerAlign: "center",
      align: "center",
      renderCell: (params) => <PriorityBadge priority={params.row.priorita} />,
    },
    {
      field: "ultimoAggiornamento",
      headerName: "Ultimo Agg.",
      minWidth: 250,
      flex: 1,
      headerAlign: "center",
      align: "center",
      renderCell: (params) => params.row.ultimoAggiornamento,
    },
    {
      field: "dataApertura",
      headerName: "Creato il",
      minWidth: 250,
      flex: 1,
      headerAlign: "center",
      align: "center",
      renderCell: (params) => params.row.dataApertura,
    },
  ];

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 4 }}>
      <Card>
        <Filters
          filtersActive={filtersActive}
          switchFilterActive={switchFilterActive}
          handleApplyFilters={handleApplyFilters}
          handleRemoveFilters={handleRemoveFilters}
          filtersLoading={filtersLoading}
          isApplyFiltersButtonDisabled={!isAnyFieldFilled}
          filterOptions={filters}
          filterData={filterData}
          setFilterData={setFilterData}
          canClearFilters={canClearFilters}
          isRemovingFilters={isRemovingFilters}
        />
      </Card>

      <Paper elevation={0}>
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
          }}
        >
          <Box sx={{ width: "100%", height: 500 }}>
            <DataGrid
              columns={columns}
              rows={rows}
              loading={loading}
              getRowId={(row) => row.idUid}
              pageSize={pageSize}
              onPageSizeChange={(newPageSize) => {
                setPageSize(newPageSize);
                setPageNumber(1);
              }}
              rowCount={totalRows}
              page={pageNumber - 1}
              onPageChange={(newPage) => setPageNumber(newPage + 1)}
              rowsPerPageOptions={[5, 10, 20]}
              pagination
              paginationMode="server"
              disableSelectionOnClick
              localeText={{ noRowsLabel: "Nessuna richiesta trovata" }}
              components={{
                NoRowsOverlay: () => (
                  <Box
                    sx={{
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "center",
                      justifyContent: "center",
                      height: "100%",
                      p: 3,
                    }}
                  >
                    <Box
                      sx={{
                        display: "inline-flex",
                        p: 2,
                        borderRadius: "50%",
                        bgcolor: "#f8fafc",
                        color: "#94a3b8",
                        mb: 2,
                      }}
                    >
                      <Search size={32} />
                    </Box>
                    <Typography
                      variant="h6"
                      sx={{ fontWeight: "bold", color: "#0f172a", mb: 0.5 }}
                    >
                      Nessuna richiesta trovata
                    </Typography>
                    <Typography sx={{ color: "#64748b" }}>
                      Prova a cambiare i filtri o i termini di ricerca.
                    </Typography>
                  </Box>
                ),
              }}
            />
          </Box>
        </Box>
      </Paper>
    </Box>
  );
}
